import { useUrlTab } from '@/hooks/useUrlState';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Tabs } from '@liminal/web-ui/primitives/Tabs';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { useUserDetail } from '@/hooks/useUsers';
import { UserHeader } from '../components/UserHeader';
import { ProfileKycSection } from '../components/ProfileKycSection';
import { WalletsBalancesSection } from '../components/WalletsBalancesSection';
import { SavingsSection } from '../components/SavingsSection';
import { TransactionHistorySection } from '../components/TransactionHistorySection';
import { InvestmentsSection } from '../components/InvestmentsSection';
import { LinkedAccountsRecipientsSection } from '../components/LinkedAccountsRecipientsSection';
import { NotificationsSection } from '../components/NotificationsSection';
import { NudgesSection } from '../components/NudgesSection';
import { ScheduledPaymentsSection } from '../components/ScheduledPaymentsSection';

const USER_TABS = [
  { value: 'profile', label: 'Profile & KYC' },
  { value: 'wallets', label: 'Wallets & Balances' },
  { value: 'savings', label: 'Savings' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'investments', label: 'Investments' },
  { value: 'accounts', label: 'Accounts & Recipients' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'nudges', label: 'Nudges' },
  { value: 'scheduled', label: 'Scheduled Payments' },
] as const;

export function UserDetailPage() {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading } = useUserDetail(userId ?? '');
  const [tab, setTab] = useUrlTab(USER_TABS, 'profile');

  if (isLoading) {
    return (
      <Stack spacing={3}>
        <Skeleton width={100} height={24} />
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="circle" width={60} height={60} />
          <Stack spacing={1}>
            <Skeleton width={200} height={32} />
            <Skeleton width={280} height={20} />
          </Stack>
        </Stack>
        <Skeleton width="100%" height={48} />
        <Skeleton width="100%" height={300} />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate('/users')}>
          Back to Users
        </Button>
      </Box>

      <UserHeader user={user} loading={false} userId={userId ?? ''} />

      <Box>
        <Tabs value={tab} onChange={setTab} aria-label="User sections" items={USER_TABS} />

        <Box sx={{ pt: 3 }}>
          {tab === 'profile' && <ProfileKycSection userId={userId ?? ''} />}
          {tab === 'wallets' && <WalletsBalancesSection userId={userId ?? ''} />}
          {tab === 'savings' && <SavingsSection userId={userId ?? ''} />}
          {tab === 'transactions' && <TransactionHistorySection userId={userId ?? ''} />}
          {tab === 'investments' && <InvestmentsSection userId={userId ?? ''} />}
          {tab === 'accounts' && <LinkedAccountsRecipientsSection userId={userId ?? ''} />}
          {tab === 'notifications' && <NotificationsSection userId={userId ?? ''} />}
          {tab === 'nudges' && <NudgesSection userId={userId ?? ''} />}
          {tab === 'scheduled' && <ScheduledPaymentsSection userId={userId ?? ''} />}
        </Box>
      </Box>
    </Stack>
  );
}
